import PropTypes from 'prop-types';
import { Component, createRef } from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { createFragmentContainer, graphql } from 'react-relay/legacy';

import { trackEvent } from 'dibs-tracking';
import { IntlContext } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link';
import ArrowLeft from 'dibs-icons/exports/legacy/ArrowLeft';
import ArrowRight from 'dibs-icons/exports/legacy/ArrowRight';

import MessageBannerItem from './MessageBannerItem';
import {
    wasShown,
    setValuesToStorage,
    addToShownList,
    getMessagesToShow,
    getPromoMessage,
} from './helpers/messageHelpers';
import { getTrackingObj, getMessageTitle } from './helpers/sharedMessageHelpers';
import { isPreview, getAllPreviewMessagesToShow } from './helpers/previewHelpers';

import style from './MessageBanner-style.scss';

const NEXT_MESSAGE_INTERVAL = 6500;

class MessageBannerCarousel extends Component {
    constructor(props) {
        super(props);

        this.state = {
            messagesToShow: [],
            curr: 0,
        };

        this.handlePrevious = this.handlePrevious.bind(this);
        this.handleNext = this.handleNext.bind(this);
        this.handleFocus = this.handleFocus.bind(this);
        this.fireTracking = this.fireTracking.bind(this);

        this.messageInterval = null;
        this.carouselRef = createRef();
    }

    componentDidMount() {
        const {
            viewer,
            user,
            messages,
            messagesToPreview,
            tradeEOYMessages,
            showAllMessages,
            useGlobalFallbackMessage,
            onMessageDisplay,
            currency,
        } = this.props;

        let messagesToShow;
        if (tradeEOYMessages) {
            messagesToShow = tradeEOYMessages;
        } else if (messagesToPreview && showAllMessages) {
            messagesToShow = getAllPreviewMessagesToShow(messagesToPreview);
        } else {
            const promotion = viewer?.activePromotion;
            const expiringPromotion = viewer?.expiringPromotion;
            messagesToShow = (
                getPromoMessage({
                    viewerRef: viewer,
                    userRef: user,
                    promotionRef: promotion,
                    expiringPromotionRef: expiringPromotion,
                    intl: this.context,
                    defaultCurrency: currency,
                }) || []
            ).concat(
                getMessagesToShow({
                    intl: this.context,
                    messageSet: messages,
                    useGlobalFallbackMessage,
                }) || []
            );
        }

        this.setState({ messagesToShow });

        onMessageDisplay({ messageTitle: getMessageTitle(messagesToShow[0]) });

        // If it is not a preview mode and there are multiple messages it will start auto message loop.
        if (!showAllMessages && messagesToShow.length > 1) {
            this.messageInterval = setInterval(this.handleNext, NEXT_MESSAGE_INTERVAL);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const { onMessageDisplay } = this.props;
        const { curr, messagesToShow } = this.state;

        if (curr !== prevState.curr) {
            onMessageDisplay({ messageTitle: getMessageTitle(messagesToShow[curr]) });
        }
    }

    componentWillUnmount() {
        if (this.messageInterval) {
            clearInterval(this.messageInterval);
        }
    }

    handlePrevious() {
        let index = this.state.curr - 1;

        if (index < 0) {
            index = this.state.messagesToShow.length - 1;
        }

        this.fireTracking(this.state.curr, 'previous', index);
        this.setState({ curr: index });
    }

    handleNext() {
        let index = this.state.curr + 1;

        if (index > this.state.messagesToShow.length - 1) {
            index = 0;
        }

        this.fireTracking(this.state.curr, 'next', index);
        this.setState({ curr: index });
    }

    handleFocus(index) {
        if (index !== this.state.curr) {
            this.setState({ curr: index });
            //prevents scrollig when using tab
            this.carouselRef.current.scrollTo(0, 0);
        }
    }

    fireTracking(previous, type, current) {
        if (!isPreview() && !this.messageInterval) {
            trackEvent(
                getTrackingObj({
                    messageItem: this.state.messagesToShow[previous],
                    action: `banner ${type} clicked`,
                })
            );
        }
        this.setItemValuesToStorage(this.state.messagesToShow[current]);
    }

    setItemValuesToStorage(messageItem) {
        if (messageItem && !isPreview() && !wasShown(messageItem)) {
            setValuesToStorage(messageItem);
            addToShownList(messageItem);

            trackEvent(
                getTrackingObj({
                    messageItem,
                    action: 'banner displayed',
                })
            );
        }
    }

    render() {
        const { showAllMessages } = this.props;
        const { curr, messagesToShow } = this.state;
        const count = messagesToShow.length;
        const hasPagination = showAllMessages && count > 1;

        const carouselItemsStyle = {
            width: `${count * 100}%`,
            transform: `translateX(${-(curr / count) * 100}%)`,
        };

        const carouselItemsClasses = classnames(style.carouselItems, {
            [style.noTransition]: !!this.messageInterval,
        });

        const wrapperClasses = classnames(style.carouselWrapper, {
            [style.hasPagination]: hasPagination,
        });

        this.setItemValuesToStorage(messagesToShow[0]);

        return (
            <div className={wrapperClasses}>
                {hasPagination ? (
                    <Link className={style.carouselArrowWrapperLeft} onClick={this.handlePrevious}>
                        <ArrowLeft className={style.carouselArrowLeft} />
                    </Link>
                ) : null}
                <div className={style.carousel} ref={this.carouselRef}>
                    <div className={carouselItemsClasses} style={carouselItemsStyle}>
                        {messagesToShow.map((item, index) => (
                            <MessageBannerItem
                                key={`${item.note.serviceId}|${index}`}
                                messageItem={item}
                                onFocus={() => this.handleFocus(index)}
                            />
                        ))}
                    </div>
                </div>
                {hasPagination ? (
                    <Link className={style.carouselArrowWrapperRight} onClick={this.handleNext}>
                        <ArrowRight className={style.carouselArrowRight} />
                    </Link>
                ) : null}
            </div>
        );
    }
}

MessageBannerCarousel.contextType = IntlContext;

MessageBannerCarousel.defaultProps = {
    showAllMessages: false,
    onMessageDisplay: () => {},
};

MessageBannerCarousel.propTypes = {
    viewer: PropTypes.object,
    user: PropTypes.object,
    messages: PropTypes.array,
    messagesToPreview: PropTypes.array,
    tradeEOYMessages: PropTypes.array,
    showAllMessages: PropTypes.bool,
    useGlobalFallbackMessage: PropTypes.bool,
    onMessageDisplay: PropTypes.func,
    currency: PropTypes.string,
};

MessageBannerCarousel = createFragmentContainer(MessageBannerCarousel, {
    viewer: graphql`
        fragment MessageBannerCarousel_viewer on Viewer {
            ...messageHelpers_viewer
            activePromotion(userId: $userId) {
                ...messageHelpers_promotion
            }
            expiringPromotion(userId: $userId) {
                ...messageHelpers_expiringPromotion
            }
        }
    `,
    user: graphql`
        fragment MessageBannerCarousel_user on User {
            ...messageHelpers_user
        }
    `,
    messages: graphql`
        fragment MessageBannerCarousel_messages on MessageSetModule @relay(plural: true) {
            userType
            items {
                impressionCap
                note {
                    serviceId
                    content {
                        title
                        message
                        cta {
                            text
                            url
                        }
                    }
                    anchors {
                        serviceId
                        content {
                            title
                            message
                            cta {
                                text
                                url
                            }
                        }
                    }
                }
            }
        }
    `,
});

const mapStateToProps = ({ currency }) => ({
    currency: currency?.currency,
});

export default connect(mapStateToProps)(MessageBannerCarousel);
